import { useMemo, useState } from 'react'
import { Form, useNavigate, useParams } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { DatePickerValue, F0Card, F0DatePicker } from '@factorialco/f0-react'
import { F0ActionBar, Input, Textarea } from '@factorialco/f0-react/dist/experimental'
import { FormikProvider } from 'formik'
import {
  ContactFragment,
  FinanceDealsSelectorFragment,
  FinanceEnumsQuoteStatusEnum,
  FinanceEnumsQuoteTypeEnum,
  LegalEntityForSelectFragment,
  QuoteDetailsPageDocument,
  SalesDocumentTemplateForSelectFragment,
  UpdateQuoteDetailsDocument,
} from 'generated/resources/graphql'
import moment from 'moment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { formattedAmount } from 'modules/banking/lib/paymentHelpers'
import { FinanceContactsSelector } from 'modules/finance/components/Contacts/FinanceContactsSelector'
import { FinanceDealsSelector } from 'modules/finance/components/Deals/FinanceDealsSelector'
import { getDefaultSalesDocumentTemplate } from 'modules/finance/components/Invoices/SaleInvoicesUpsertSidepanel/lib/defaultValuesUtils'
import { ConvertToProjectModal } from 'modules/finance/components/Quotes/modals/ConvertToProjectModal'
import { FinanceSalesDocumentTemplateSelector } from 'modules/finance/components/SalesDocumentTemplates/FinanceSalesDocumentTemplateSelector'
import CurrencySelector from 'modules/finance/components/selectors/CurrencySelector'
import { FinanceLegalEntitySelector } from 'modules/finance/components/selectors/FinanceLegalEntitySelector'
import { QuoteStatusSelector } from 'modules/finance/components/selectors/QuoteStatusSelector'
import { QuoteTypeSelector } from 'modules/finance/components/selectors/QuoteTypeSelector'
import { useDownloadQuotePdf } from 'modules/finance/hooks/quotes/useDownloadQuotePdf'
import { onHeaderActionClick } from 'modules/finance/lib/quotesUtils'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { useDeleteQuoteDialog } from 'modules/finance/hooks/quotes/useDeleteQuoteDialog'
import { useConvertQuoteIntoInvoice } from 'modules/finance/hooks/quotes/useConvertQuoteIntoInvoice'

const QuoteDetailsPage = () => {
  const { quoteId } = useParams()
  const { company, access } = useCurrentSession()
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const locale = access.locale

  const [showConvertToProjectModal, setShowConvertToProjectModal] = useState(false)
  const downloadQuotePdf = useDownloadQuotePdf()
  const { showDeleteQuoteDialog } = useDeleteQuoteDialog()
  const convertQuoteIntoInvoice = useConvertQuoteIntoInvoice()

  const navigateToProject = () => {
    if (quote?.projectId) {
      navigate(
        navigation.projects.id.details.overview.fullPath({
          id: String(quote.projectId),
        })
      )
    }
  }
  const navigateToInvoice = () => {
    if (quoteFinancialDocumentId) {
      navigate(
        navigation.finance.sales.salesInvoices.detail.fullPath({
          invoiceId: Number(quoteFinancialDocumentId),
        })
      )
    }
  }
  const convertToInvoice = () => {
    if (!quoteId || quoteFinancialDocumentId) return
    convertQuoteIntoInvoice(Number(quoteId))
  }
  const {
    data: quoteDetailsData,
    error,
    loading,
  } = useQuery(QuoteDetailsPageDocument, {
    variables: {
      id: quoteId ? Number(quoteId) : -1,
    },
    skip: !quoteId,
  })
  const quote = quoteDetailsData?.finance.quote
  const totalAmountCents = Number(quote?.totalAmountCents ?? 0)
  const baseCostCents = Number(quote?.baseCostCents ?? 0)

  const quoteFinancialDocumentId = quote?.financialDocumentAssociation?.financialDocument?.id
  const authorized = quote?._policies?.manage.can
  const canSeeDeals = quoteDetailsData?.finance?._policies?.sidebarSeeFinanceDealsSubLevelItem.can
  const taxBridgeActivations = quoteDetailsData?.finance.taxBridgeActivations ?? []
  const defaultSalesDocumentTemplates =
    quoteDetailsData?.finance.defaultSalesDocumentTemplates ?? []
  const defaultSalesDocumentTemplate = getDefaultSalesDocumentTemplate(
    quote?.legalEntity,
    defaultSalesDocumentTemplates
  )
  const editable = quote?.status == 'draft'

  const [updateQuoteDetails] = useMutation(UpdateQuoteDetailsDocument, {
    extractorFn: (mutationData) => mutationData.financeMutations.updateDetailsFinanceQuote,
  })

  const onSubmit = async (values: QuoteFormFields) => {
    const isAccepted = values.status === 'accepted'
    if (
      !values.name ||
      !values.quoteType ||
      !values.status ||
      !values.currency ||
      !values.legalEntity
    ) {
      return { success: false, data: {}, errors: [] }
    }
    if (
      isAccepted &&
      (!values.quoteNumber || !values.contact || !values.documentDate || !values.expirationDate)
    ) {
      return { success: false, data: {}, errors: [] }
    }

    const result = await updateQuoteDetails({
      variables: {
        id: Number(quoteId),
        name: values.name,
        description: values.description,
        quoteType: values.quoteType,
        status: values.status,
        dealId: values.deal?.id,
        currency: values.currency,
        quoteNumber: values.quoteNumber,
        expirationDate: values.expirationDate,
        documentDate: values.documentDate,
        legalEntityId: values.legalEntity.id,
        contactId: values.contact?.id,
        memo: values.memo,
        footer: values.footer,
      },
    })

    const handled = handleResult(result, (errors) => {
      form.setErrors({ ...errors })
    })
    if (handled.success) {
      track(trackingEvents.accounting.quotes.submitEdit)

      app.setFlash({
        type: 'correct',
        message: i18n.t('finance.quotes.notifications.saved_successfully'),
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_error'),
      })
    }
    return handled
  }

  const validateForm = (validationData: QuoteFormFields) => {
    const errors: FormErrors<QuoteFormFields> = {}
    if (!validationData.name) {
      errors.name = i18n.t('finance.sale_invoices.form.error_required')
    }

    if (!validationData.legalEntity) {
      errors.legalEntity = i18n.t('finance.sale_invoices.form.error_required')
    }

    if (!validationData.currency) {
      errors.currency = i18n.t('finance.sale_invoices.form.error_required')
    }

    const isAccepted = validationData.status === 'accepted'
    if (isAccepted) {
      if (!validationData.quoteNumber) {
        errors.quoteNumber = i18n.t('finance.sale_invoices.form.error_required')
      }

      if (!validationData.documentDate) {
        errors.documentDate = i18n.t('finance.sale_invoices.form.error_required')
      }

      if (!validationData.contact) {
        errors.contact = i18n.t('finance.sale_invoices.form.error_required')
      }
    }

    return errors
  }

  interface QuoteFormFields {
    name?: string | null
    description?: string | null
    status?: FinanceEnumsQuoteStatusEnum
    quoteType?: FinanceEnumsQuoteTypeEnum
    currency?: string | null
    quoteNumber?: string | null
    expirationDate?: string | null
    documentDate?: string | null
    legalEntity?: LegalEntityForSelectFragment | null //issuer
    contact?: ContactFragment | null //receiver / recipient
    deal?: FinanceDealsSelectorFragment | null
    memo?: string | null
    footer?: string | null
    salesDocumentTemplate?: SalesDocumentTemplateForSelectFragment | null
  }

  const form = useForm<QuoteFormFields>({
    enableReinitialize: true,
    defaultValues: {
      name: quote?.name,
      description: quote?.description,
      status: quote?.status,
      quoteType: quote?.quoteType,
      currency: quote?.currency || quote?.legalEntity?.currency,
      quoteNumber: quote?.quoteNumber,
      documentDate: quote?.documentDate,
      expirationDate: quote?.expirationDate,
      legalEntity: quote?.legalEntity,
      contact: quote?.client,
      deal: quote?.deal,
      memo: quote?.memo,
      footer: quote?.footer,
      salesDocumentTemplate: defaultSalesDocumentTemplate,
    },
    validate: validateForm,
    validateOnChange: false,
    onSubmit: async (data) => {
      return await onSubmit(data)
    },
  })

  const showActivateLegalEntity = useMemo(() => {
    const legalEntity = form.values.legalEntity
    if (legalEntity?.country !== 'es') return false

    return !taxBridgeActivations.some(
      (activation) => activation.legalEntityId === legalEntity.id && activation.activatedAt
    )
  }, [form.values.legalEntity, taxBridgeActivations])

  const minDate = moment().subtract(180, 'days').toDate()
  const maxDate = moment().add(5, 'years').toDate()

  const stringDateToDatePicker = (date: string | null | undefined): DatePickerValue | undefined => {
    if (!date) {
      return undefined
    }
    const parsedDate = stringToDate(date, 'backend')
    if (!parsedDate.isValid()) {
      return undefined
    }
    const normalizedDate = parsedDate.toDate()
    return {
      granularity: 'day' as const,
      value: {
        from: normalizedDate,
        to: normalizedDate,
      },
    }
  }

  const datePickerDateToString = (date: DatePickerValue | undefined) => {
    const selectedDate = date?.value?.from

    if (!selectedDate) {
      form.setFieldValue('startDate', null)
      return
    }

    return i18nDateToString(selectedDate, 'backend')
  }

  const handleContactSelect = (contact: ContactFragment | null) => {
    // Deal client and contact id must be the same, to avoid issues we reset the selected deal if they are different
    const dealClientId = form.values.deal?.client?.id
    const contactId = contact?.id

    if (dealClientId === contactId) {
      form.setValues({ ...form.values, contact: contact ?? undefined })
    } else {
      form.setValues({ ...form.values, contact: contact ?? undefined, deal: undefined })
    }
  }

  const handleDealSelect = (deal: FinanceDealsSelectorFragment | null) => {
    // Deal client and contact id must be the same, to avoid issues we reset the selected contact if they are different
    // If no contact is selected, we set the deal and the contact to the deal client

    const contactId = form.values.contact?.id
    const dealClientId = deal?.client?.id

    if (!contactId) {
      form.setValues({
        ...form.values,
        deal: deal ?? undefined,
        contact: deal?.client ?? undefined,
      })
    } else if (dealClientId === contactId) {
      form.setValues({ ...form.values, deal: deal ?? undefined })
    } else {
      form.setValues({ ...form.values, deal: deal ?? undefined, contact: undefined })
    }
  }

  const handleSalesDocumentTemplateSelect = (
    salesDocumentTemplate: SalesDocumentTemplateForSelectFragment | null
  ) => {
    form.setValues({
      ...form.values,
      salesDocumentTemplate: salesDocumentTemplate,
      footer: salesDocumentTemplate?.footer ?? undefined,
    })
  }

  return (
    <ResponsivePage.Body
      variant="fullscreen"
      authorized={authorized}
      loading={loading}
      error={error}
      onActionClick={(key) =>
        onHeaderActionClick(
          key,
          Number(quoteId),
          () => setShowConvertToProjectModal(true),
          downloadQuotePdf,
          () =>
            showDeleteQuoteDialog({
              quoteId: Number(quoteId),
              callback: () => navigate(navigation.finance.sales.quotes.fullPath()),
            }),
          navigateToProject,
          navigateToInvoice,
          convertToInvoice
        )
      }
    >
      <FormikProvider value={form}>
        <Form>
          {/* TODO: change Layout */}
          <Box width="full" flexDirection="row" gap="s16" padding="s16">
            <Box width="full" flexDirection="column" gap="s16">
              <F0Card title={i18n.t('finance.quotes.form.basic_information.section_title')}>
                <Box width="full" flexDirection="column" gap="s16">
                  <Box width="full" flexDirection="row" gap="s16">
                    <Box width="full">
                      <Input
                        label={i18n.t('finance.quotes.form.basic_information.name')}
                        type="text"
                        size="md"
                        value={form.values.name || ''}
                        onChange={(value) => form.setValues({ ...form.values, name: value })}
                        error={form.errors.name}
                        placeholder={'Name'}
                        disabled={!editable}
                        required
                      />
                    </Box>
                    <Box width="full">
                      <Input
                        label={i18n.t('finance.quotes.form.basic_information.quote_number')}
                        type="text"
                        size="md"
                        value={form.values.quoteNumber || ''}
                        onChange={(value) => form.setValues({ ...form.values, quoteNumber: value })}
                        error={form.errors.quoteNumber}
                        placeholder={'QTE-250219'}
                        disabled={!editable}
                      />
                    </Box>
                  </Box>
                  <Box width="full" flexDirection="row" gap="s16">
                    <Box width="full">
                      <F0DatePicker
                        label={i18n.t('finance.quotes.form.basic_information.issued_date')}
                        size="md"
                        value={stringDateToDatePicker(form.values.documentDate)}
                        onChange={(value) =>
                          form.setValues({
                            ...form.values,
                            documentDate: datePickerDateToString(value),
                          })
                        }
                        error={form.errors.documentDate}
                        minDate={minDate}
                        maxDate={
                          form.values.expirationDate
                            ? new Date(form.values.expirationDate)
                            : maxDate
                        }
                        disabled={!editable}
                      />
                    </Box>
                    <Box width="full">
                      <F0DatePicker
                        label={i18n.t('finance.quotes.form.basic_information.expiration_date')}
                        size="md"
                        value={stringDateToDatePicker(form.values.expirationDate)}
                        onChange={(value) =>
                          form.setValues({
                            ...form.values,
                            expirationDate: datePickerDateToString(value),
                          })
                        }
                        error={form.errors.documentDate}
                        minDate={
                          form.values.documentDate ? new Date(form.values.documentDate) : minDate
                        }
                        maxDate={maxDate}
                        disabled={!editable}
                      />
                    </Box>
                  </Box>
                  <Box width="full">
                    <Textarea
                      label={i18n.t('finance.quotes.form.basic_information.description')}
                      value={form.values.description || ''}
                      onChange={(value) => form.setValues({ ...form.values, description: value })}
                      error={form.errors.description}
                      disabled={!editable}
                    />
                  </Box>
                </Box>
              </F0Card>
              <F0Card
                title={i18n.t('finance.quotes.form.financial_settings.section_title')}
                secondaryActions={
                  showActivateLegalEntity
                    ? {
                        href: navigation.settings.finance.sales.fullPath(),
                        label: i18n.t('finance.quotes.form.financial_settings.legal_entity_activate'),
                        target: '_blank',
                        disabled: !showActivateLegalEntity,
                      }
                    : undefined
                }>
                <Box width="full" flexDirection="column" gap="s16">
                  <FinanceLegalEntitySelector
                    label={i18n.t('finance.quotes.form.financial_settings.issuer_legal_entity')}
                    value={form.values.legalEntity ?? null}
                    onSelect={async (value) =>
                      form.setValues({
                        ...form.values,
                        legalEntity: value,
                        currency: form.values.currency || value?.currency,

                        salesDocumentTemplate: undefined,
                      })
                    }
                    error={form.errors.legalEntity}
                    hideSearchIcon
                    required
                    disabled={!editable}
                    queryParams={{}}
                  />
                  <Box width="full" flexDirection="row" gap="s16">
                    <Box width="full">
                      <Input
                        label={i18n.t(
                          'finance.quotes.form.financial_settings.legal_entity_currency'
                        )}
                        type="text"
                        size="md"
                        value={form.values.legalEntity?.currency ?? ''}
                        disabled
                      />
                    </Box>
                    <Box width="full">
                      <CurrencySelector
                        key={form.values.currency ?? 'quoteCurrency'}
                        label={i18n.t('finance.quotes.form.financial_settings.quote_currency')}
                        value={form.values.currency ?? ''}
                        error={form.errors.currency}
                        onChange={(value) => form.setValues({ ...form.values, currency: value })}
                        disabled={!editable}
                      />
                    </Box>
                  </Box>
                </Box>
              </F0Card>
              <F0Card title={i18n.t('finance.quotes.form.document.section_title')}>
                <Box width="full" flexDirection="column" gap="s16">
                  <FinanceSalesDocumentTemplateSelector
                    label={i18n.t('finance.sale_invoices.form.template.title')}
                    emptyLabel={i18n.t('finance.sale_invoices.form.template.select_a_template')}
                    value={form.values.salesDocumentTemplate ?? null}
                    onSelect={handleSalesDocumentTemplateSelect}
                    queryParams={{
                      legalEntityIds: form.values.legalEntity?.id
                        ? [form.values.legalEntity.id]
                        : undefined,
                    }}
                    disableCreate
                    hideSearchIcon
                    disabled={!editable}
                  />
                  <Box width="full">
                    <Textarea
                      label={i18n.t('finance.sale_invoices.form.memo')}
                      onChange={(value) => {
                        if (typeof value == 'string')
                          form.setValues({ ...form.values, memo: value })
                      }}
                      placeholder={i18n.t('finance.sale_invoices.form.text_area_placeholder')}
                      value={form.values.memo || ''}
                      disabled={!editable}
                    />
                  </Box>
                  <Box width="full">
                    <Textarea
                      label={i18n.t('finance.sale_invoices.form.footer')}
                      onChange={(value) => {
                        if (typeof value == 'string')
                          form.setValues({ ...form.values, footer: value })
                      }}
                      placeholder={i18n.t('finance.sale_invoices.form.text_area_placeholder')}
                      value={form.values.footer || ''}
                      disabled={!editable}
                    />
                  </Box>
                </Box>
              </F0Card>
            </Box>
            <Box width="s400" flexDirection="column" gap="s16">
              <F0Card title={i18n.t('finance.quotes.form.management.section_title')}>
                <Box width="full" flexDirection="column" gap="s16">
                  <QuoteStatusSelector
                    key={form.values.status ?? 'status'}
                    label={i18n.t('finance.quotes.form.management.status')}
                    value={form.values.status}
                    onChange={(value) => form.setValues({ ...form.values, status: value })}
                    error={form.errors.status}
                    disabled={!editable}
                  />
                  <QuoteTypeSelector
                    key={form.values.quoteType ?? 'quoteType'}
                    label={i18n.t('finance.quotes.form.management.budget')}
                    value={form.values.quoteType}
                    onChange={(value) => form.setValues({ ...form.values, quoteType: value })}
                    error={form.errors.quoteType}
                    disabled={!editable}
                  />
                </Box>
              </F0Card>
              <F0Card
                title={i18n.t('finance.quotes.form.client_and_deal.section_title')}
                secondaryActions={
                  canSeeDeals
                    ? {
                        href: form.values.deal?.id
                          ? `/finance/sales/deals/${form.values.deal.id}`
                          : '#',
                        label: i18n.t('finance.quotes.form.client_and_deal.edit'),
                        target: '_blank',
                        disabled: !form.values.deal?.id,
                      }
                    : undefined
                }
              >
                <Box width="full" flexDirection="column" gap="s16">
                  <FinanceContactsSelector
                    value={form.values.contact}
                    onSelect={handleContactSelect}
                    label={i18n.t('finance.quotes.form.client_and_deal.client')}
                    type="finance_contact"
                    queryParams={{
                      companyId: company.id,
                      contactCreateType: 'client',
                    }}
                    error={form.errors.contact}
                    hideSearchIcon
                    disabled={!editable}
                  />
                  {canSeeDeals && (
                    <Box width="full" flexDirection="column" gap="s16">
                      <FinanceDealsSelector
                        value={form.values.deal}
                        onSelect={handleDealSelect}
                        label={i18n.t('finance.quotes.form.client_and_deal.deal')}
                        type="finance_deal"
                        queryParams={{
                          clientIds: form.values.contact?.id
                            ? [form.values.contact?.id]
                            : undefined,
                        }}
                        excludeClosedDeals
                        error={form.errors.deal}
                        hideSearchIcon
                        disabled={!editable}
                      />
                      <Box width="full">
                        <Input
                          label={i18n.t('finance.quotes.form.client_and_deal.estimated_deal_value')}
                          type="text"
                          size="md"
                          value={formattedAmount({
                            amountCents: Number(form.values.deal?.estimatedAmountCents),
                            currency: form.values.deal?.currency ?? '',
                            locale,
                          })}
                          disabled
                        />
                      </Box>
                      <Box width="full">
                        <Input
                          label={i18n.t(
                            'finance.quotes.form.client_and_deal.estimated_deal_close_date'
                          )}
                          type="text"
                          size="md"
                          value={
                            form.values.deal?.expectedCloseDate
                              ? moment(form.values.deal.expectedCloseDate).format('DD/MM/YYYY')
                              : ''
                          }
                          disabled
                        />
                      </Box>
                    </Box>
                  )}
                </Box>
              </F0Card>
            </Box>
          </Box>
          {editable && form.dirty && (
            <F0ActionBar
              isOpen
              primaryActions={[
                {
                  label: i18n.t('forms.action_bar.save'),
                  onClick: () => form.submitForm(),
                  disabled: form.isSubmitting,
                },
              ]}
              secondaryActions={[
                {
                  label: i18n.t('forms.action_bar.discard'),
                  onClick: () => form.resetForm(),
                  disabled: form.isSubmitting,
                },
              ]}
              label={i18n.t('forms.action_bar.label')}
            />
          )}
        </Form>
      </FormikProvider>
      {showConvertToProjectModal && quote && quote.currency && quote.name && (
        <ConvertToProjectModal
          isOpen={showConvertToProjectModal}
          onClose={() => setShowConvertToProjectModal(false)}
          currency={quote.currency}
          quoteId={quote.id}
          defaultProjectName={quote.name}
          totalAmountCents={totalAmountCents}
          baseCostCents={baseCostCents}
          totalHours={quote.totalHours}
        />
      )}
    </ResponsivePage.Body>
  )
}

export default QuoteDetailsPage
